import { inject, Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Action, Selector, State, StateContext } from '@ngxs/store';

import { SeasonsStateModel } from './seasons.model';
import { AddOrUpdateSeason, LoadSeasons, RemoveSeason, SelectSeason, SetSeasons } from './seasons.actions';
import { SeasonsService } from '@features/seasons/services/seasons.service';
import { Season } from '@features/seasons/models/season.model';

const initialState: SeasonsStateModel = {
  seasons: [],
  selectedSeasonId: null,
  loading: false,
};

@State<SeasonsStateModel>({
  name: 'SeasonsState',
  defaults: initialState,
})
@Injectable()
export class SeasonsState {
  private seasonsService = inject(SeasonsService);

  @Selector([SeasonsState])
  static getSeasons(state: SeasonsStateModel): Season[] {
    return state.seasons;
  }

  @Selector([SeasonsState])
  static getSelectedSeasonId(state: SeasonsStateModel): string | null {
    return state.selectedSeasonId;
  }

  @Selector([SeasonsState])
  static getSelectedSeason(state: SeasonsStateModel): Season | null {
    if (!state.selectedSeasonId) return null;
    return state.seasons.find((s) => s.id === state.selectedSeasonId) ?? null;
  }

  @Selector([SeasonsState])
  static isLoading(state: SeasonsStateModel): boolean {
    return state.loading;
  }

  @Action(LoadSeasons)
  loadSeasons(context: StateContext<SeasonsStateModel>) {
    context.patchState({ loading: true });
    return this.seasonsService.getSeasons().pipe(
      tap((seasons) => context.patchState({ seasons, loading: false })),
      catchError(() => {
        context.patchState({ loading: false });
        return EMPTY;
      })
    );
  }

  @Action(SetSeasons)
  setSeasons(context: StateContext<SeasonsStateModel>, { seasons }: SetSeasons) {
    context.patchState({ seasons });
  }

  @Action(SelectSeason)
  selectSeason(context: StateContext<SeasonsStateModel>, { seasonId }: SelectSeason) {
    context.patchState({ selectedSeasonId: seasonId });
  }

  @Action(AddOrUpdateSeason)
  addOrUpdateSeason(context: StateContext<SeasonsStateModel>, { season }: AddOrUpdateSeason) {
    const state = context.getState();
    const exists = state.seasons.find((s) => s.id === season.id);
    const updated = exists
      ? state.seasons.map((s) => (s.id === season.id ? season : s))
      : [season, ...state.seasons];
    context.patchState({ seasons: updated });
  }

  @Action(RemoveSeason)
  removeSeason(context: StateContext<SeasonsStateModel>, { seasonId }: RemoveSeason) {
    const state = context.getState();
    const updatedSeasons = state.seasons.filter((s) => s.id !== seasonId);
    const selectedSeasonId = state.selectedSeasonId === seasonId ? null : state.selectedSeasonId;
    context.patchState({ seasons: updatedSeasons, selectedSeasonId });
  }
}

export * from './seasons.actions';
